/**
 * lib/pult-effects.ts — the pult's four effects in numbers: where BEATS, DEPTH and a deck's FX
 * paddle stand, and the deck's tempo → what each effect's nodes are set to (task 58).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Эффекты»; stories 175–178. Pure, as
 * `lib/pult-mixer.ts` is: `engine/mixer.ts` builds the nodes and writes these numbers into them;
 * `test/pult-effects.test.ts` pins them.
 *
 * ## Beside the dry sound, after the deck's gain
 *
 * Each deck has all four effects, each behind a send of its own, taken after the deck's gain —
 * so LEVEL, the fader and the crossfader shape what goes in — and returned straight to MAIN, beside
 * the dry sound, through one gain of DEPTH. The design draws the split at the deck's door; here it
 * is after the gain, because ECHO OUT takes the deck's gain to 0 and its echo must ring on: a
 * return through that gain would cut the tail with the deck (executor's choice). An effect is a
 * send: the paddle up opens the chosen effect's, down closes it, and what is inside — the echo's
 * repeats, the hall — rings out by itself. Nothing is torn down: the four are built once per deck.
 *
 * ## The tempo
 *
 * BEATS is a length in beats of the deck's track as measured (`lib/mix.ts`, {@link sureBeat}) at
 * the speed the deck plays it — the tempo, a mix's factor, PITCH BEND; a track with no sure measure
 * counts {@link DEFAULT_BPM}. The knob goes by steps ({@link BEAT_STEPS}), its middle one beat.
 *
 * ## The four
 *
 * - **ECHO**: a delay of BEATS beats, at most {@link ECHO_MAX_S} (the delay node is made for it),
 *   fed back at DEPTH × 0,7 — the repeats die away, never build up.
 * - **REVERB**: a convolver with a response computed here — two channels of noise dying 60 dB in
 *   two seconds. No file, no network. The design computes it in an `OfflineAudioContext`; plain
 *   noise needs no rendering, and arithmetic here is what a test can read (executor's choice).
 * - **FLANGER**: a delay of 1–5 ms swung by a 0,25 Hz oscillator, fed back at 0,5 — the jet.
 *   **Not checked by ear:** Web Audio holds a delay inside a loop to one block, 128 samples — 2,7 ms
 *   at 48 000 Hz — so the bottom of the swing may be flattened.
 * - **PHASER**: four allpass filters swung between 300 and 3000 Hz, one turn in BEATS beats.
 *
 * ## ECHO OUT, and its tail
 *
 * An echo opened at the moment the deck goes silent would take in 50 ms of music and repeat that
 * scrap — the first review found it. So each deck has one more delay, the tail's: fed by the deck
 * all the time, its loop and its way out shut, it always holds the last BEATS of the track. ECHO OUT
 * opens its way out and its loop ({@link DeckEffectSettings.tail}) and only then takes the deck's
 * gain to 0 in 50 ms ({@link ECHO_OUT_FADE_S}): what played last repeats and dies away, as on a DJ's
 * echo out. At least {@link ECHO_OUT_DEPTH} of it is heard — at DEPTH 0 there would be no tail at all
 * (executor's choice). After {@link echoOutTailS} the deck pauses and the tail shuts
 * (`engine/player.ts`). The paddle and the effect keys are not moved by it: the tail is its own.
 */

import type { TrackBeat } from './api';
import { sureBeat } from './mix';
import type { DeckSide } from './pult-layout';

export type PultEffect = 'echo' | 'reverb' | 'flanger' | 'phaser';

/** The four, in the order of their keys on the board. */
export const PULT_EFFECTS: readonly PultEffect[] = Object.freeze([
  'echo',
  'reverb',
  'flanger',
  'phaser',
]);

/** The tempo a track with no sure measure counts. */
export const DEFAULT_BPM = 95;

/** The longest echo: the delay node is made this long. */
export const ECHO_MAX_S = 4;

/** The echo's repeats: fed back at DEPTH times this. */
export const ECHO_FEEDBACK = 0.7;

/** BEATS, left to right: an eighth of a beat to four beats. The middle step is one beat. */
export const BEAT_STEPS: readonly number[] = Object.freeze([
  1 / 8,
  1 / 4,
  1 / 2,
  3 / 4,
  1,
  2,
  3,
  4,
]);

/** REVERB's hall: its response dies 60 dB in this long. */
export const REVERB_S = 2;

/** ECHO OUT: the deck's gain to 0 in this long. */
export const ECHO_OUT_FADE_S = 0.05;

/** ECHO OUT: the echo is heard at least this much. */
export const ECHO_OUT_DEPTH = 0.6;

/** ECHO OUT: the deck pauses after this many of its echo's repeats. */
export const ECHO_OUT_REPEATS = 4;

function share(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.5;
}

/** BEATS at `position` (0…1), as a number of beats. */
export function beatsAt(position: number): number {
  const index = Math.round(share(position) * (BEAT_STEPS.length - 1));
  return BEAT_STEPS[index] ?? 1;
}

/** Where BEATS stands for `beats` — the nearest step's place. */
export function beatsKnob(beats: number): number {
  let best = 0;
  BEAT_STEPS.forEach((step, i) => {
    if (Math.abs(step - beats) < Math.abs((BEAT_STEPS[best] ?? 1) - beats)) best = i;
  });
  return best / (BEAT_STEPS.length - 1);
}

/** The deck's tempo in beats a minute: its track's sure measure, or {@link DEFAULT_BPM}, × `speed`. */
export function deckBpm(beat: TrackBeat | null | undefined, speed: number): number {
  const bpm = sureBeat(beat) ? beat.bpm : DEFAULT_BPM;
  return bpm * (Number.isFinite(speed) && speed > 0 ? speed : 1);
}

/** Seconds of `beats` at `bpm`. */
function beatSeconds(beats: number, bpm: number): number {
  return (beats * 60) / (bpm > 0 ? bpm : DEFAULT_BPM);
}

export interface EchoSettings {
  readonly delayS: number;
  readonly feedback: number;
}

export function echoSettings(beats: number, bpm: number, depth: number): EchoSettings {
  return {
    delayS: Math.min(ECHO_MAX_S, beatSeconds(beats, bpm)),
    feedback: share(depth) * ECHO_FEEDBACK,
  };
}

/** FLANGER: no knob moves it. */
export const FLANGER = Object.freeze({
  /** The delay at the middle of its swing… */
  baseS: 0.003,
  /** …and how far the oscillator swings it either way: 1–5 ms. */
  sweepS: 0.002,
  lfoHz: 0.25,
  feedback: 0.5,
});

/** PHASER's filters; only the turn of its oscillator follows the knobs ({@link phaserLfoHz}). */
export const PHASER = Object.freeze({
  stages: 4,
  /** The filters' frequency at the middle of the swing… */
  centreHz: 1650,
  /** …and how far the oscillator swings it either way: 300–3000 Hz. */
  sweepHz: 1350,
});

/** PHASER: one turn in `beats` beats at `bpm`. */
export function phaserLfoHz(beats: number, bpm: number): number {
  return 1 / beatSeconds(beats, bpm);
}

/**
 * REVERB's response at `rate` samples a second: two channels of noise, each its own, dying 60 dB
 * over {@link REVERB_S} — `random` in 0…1, `Math.random` in the app, a seeded one in a test.
 */
export function reverbResponse(rate: number, random: () => number = Math.random): Float32Array[] {
  const length = Math.max(1, Math.round(rate * REVERB_S));
  // e^(−6,91) is 1/1000: 60 dB down at the end.
  const fall = Math.log(1000) / length;
  return [0, 1].map(() => {
    const channel = new Float32Array(length);
    for (let i = 0; i < length; i += 1) channel[i] = (random() * 2 - 1) * Math.exp(-fall * i);
    return channel;
  });
}

/** ECHO OUT: how long the tail rings before the deck pauses. */
export function echoOutTailS(beats: number, bpm: number): number {
  return ECHO_OUT_REPEATS * Math.min(ECHO_MAX_S, beatSeconds(beats, bpm));
}

export interface DeckEffectInput {
  /** The effect chosen by the four keys — one for both decks. */
  readonly effect: PultEffect;
  /** The deck's FX paddle: 0 down, 1 up. */
  readonly lever: number;
  /** BEATS and DEPTH, 0…1. */
  readonly beats: number;
  readonly depth: number;
  /** The deck's tempo ({@link deckBpm}). */
  readonly bpm: number;
  /** ECHO OUT's tail is open: the deck is going out in its echo. */
  readonly tail: boolean;
}

export interface DeckEffectSettings {
  /** Each effect's send: 1 open, 0 shut. */
  readonly sends: Readonly<Record<PultEffect, number>>;
  /** DEPTH: the gain of every effect's return. */
  readonly wet: number;
  readonly echo: EchoSettings;
  /** PHASER's turn. */
  readonly phaserHz: number;
  /** ECHO OUT's tail: its way out (0 shut) and its loop — its delay is the echo's. */
  readonly tail: { readonly gate: number; readonly feedback: number };
}

/** One deck's four effects and its tail, all their numbers. */
export function deckEffects(input: DeckEffectInput): DeckEffectSettings {
  const up = share(input.lever) >= 0.5;
  const depth = share(input.depth);
  const beats = beatsAt(input.beats);
  const open = (effect: PultEffect): number => (up && input.effect === effect ? 1 : 0);
  const heard = Math.max(depth, ECHO_OUT_DEPTH);
  return {
    sends: {
      echo: open('echo'),
      reverb: open('reverb'),
      flanger: open('flanger'),
      phaser: open('phaser'),
    },
    wet: depth,
    echo: echoSettings(beats, input.bpm, depth),
    phaserHz: phaserLfoHz(beats, input.bpm),
    tail: input.tail ? { gate: heard, feedback: heard * ECHO_FEEDBACK } : { gate: 0, feedback: 0 },
  };
}

/** The ids of the effects' controls on the board (`lib/pult-layout.ts`). */
export const BEATS_KNOB = 'fx.beats';
export const DEPTH_KNOB = 'fx.depth';

/** A deck's FX paddle. */
export function leverOf(side: DeckSide): string {
  return `${side}.fx`;
}
